/**
 * Audio Player Coordinator
 *
 * Coordinates multiple audio player instances on a single page to ensure:
 * 1. Only one player plays at a time (others auto-pause)
 * 2. Clear visual indication of which player is active
 * 3. Proper cleanup when players unmount
 *
 * Used primarily on SessionHistoryPage where multiple players are rendered.
 */

import React, { createContext, useContext, useCallback, useState, useRef, ReactNode } from 'react';

// Player control interface
export interface PlayerControls {
  pause: () => void;
  stop: () => void;
}

// Context value interface
interface AudioPlayerCoordinatorContextValue {
  registerPlayer: (id: string, controls: PlayerControls) => void;
  unregisterPlayer: (id: string) => void;
  notifyPlayStarted: (id: string) => void;
  notifyPlayEnded: (id: string) => void;
  currentlyPlayingId: string | null;
}

// Create context
const AudioPlayerCoordinatorContext = createContext<AudioPlayerCoordinatorContextValue | null>(null);

// Provider props
interface AudioPlayerCoordinatorProviderProps {
  children: ReactNode;
  /** Whether to auto-pause other players when one starts (default: true) */
  autoPauseOthers?: boolean;
}

/**
 * Provider component that wraps pages with multiple audio players
 */
export const AudioPlayerCoordinatorProvider: React.FC<AudioPlayerCoordinatorProviderProps> = ({
  children,
  autoPauseOthers = true
}) => {
  const [currentlyPlayingId, setCurrentlyPlayingId] = useState<string | null>(null);
  const playersRef = useRef<Map<string, PlayerControls>>(new Map());

  const registerPlayer = useCallback((id: string, controls: PlayerControls) => {
    playersRef.current.set(id, controls);
    console.log(`🎵 AudioCoordinator: Registered player "${id}" (total: ${playersRef.current.size})`);
  }, []);

  const unregisterPlayer = useCallback((id: string) => {
    playersRef.current.delete(id);
    console.log(`🎵 AudioCoordinator: Unregistered player "${id}" (total: ${playersRef.current.size})`);

    // Clear currently playing if this player was active
    setCurrentlyPlayingId(current => current === id ? null : current);
  }, []);

  const notifyPlayStarted = useCallback((id: string) => {
    console.log(`🎵 AudioCoordinator: Player "${id}" started playing`);

    if (autoPauseOthers) {
      // Pause all OTHER players
      let pausedCount = 0;
      for (const [playerId, controls] of playersRef.current) {
        if (playerId !== id) {
          controls.pause();
          pausedCount++;
        }
      }

      if (pausedCount > 0) {
        console.log(`🎵 AudioCoordinator: Paused ${pausedCount} other player(s)`);
      }
    }

    setCurrentlyPlayingId(id);
  }, [autoPauseOthers]);

  const notifyPlayEnded = useCallback((id: string) => {
    console.log(`🎵 AudioCoordinator: Player "${id}" ended/paused`);

    // Only clear if this is the currently playing player
    setCurrentlyPlayingId(current => current === id ? null : current);
  }, []);

  const value: AudioPlayerCoordinatorContextValue = {
    registerPlayer,
    unregisterPlayer,
    notifyPlayStarted,
    notifyPlayEnded,
    currentlyPlayingId
  };

  return (
    <AudioPlayerCoordinatorContext.Provider value={value}>
      {children}
    </AudioPlayerCoordinatorContext.Provider>
  );
};

/**
 * Hook to access the audio player coordinator
 *
 * @returns Coordinator context or null if not within a provider
 */
export const useAudioPlayerCoordinator = (): AudioPlayerCoordinatorContextValue | null => {
  return useContext(AudioPlayerCoordinatorContext);
};

/**
 * Hook with coordinator integration for audio players
 *
 * @param playerId Unique identifier for this player instance
 * @param controls Player control functions (pause, stop)
 * @returns Object with coordinator methods and current playing state
 */
export const usePlayerCoordination = (
  playerId: string,
  controls: PlayerControls
) => {
  const coordinator = useAudioPlayerCoordinator();
  const hasRegisteredRef = useRef(false);

  // Register/unregister on mount/unmount
  React.useEffect(() => {
    if (coordinator && !hasRegisteredRef.current) {
      coordinator.registerPlayer(playerId, controls);
      hasRegisteredRef.current = true;

      return () => {
        coordinator.unregisterPlayer(playerId);
        hasRegisteredRef.current = false;
      };
    }
  }, [coordinator, playerId, controls]);

  // Helper to notify when playback starts
  const notifyPlayStarted = useCallback(() => {
    coordinator?.notifyPlayStarted(playerId);
  }, [coordinator, playerId]);

  // Helper to notify when playback ends
  const notifyPlayEnded = useCallback(() => {
    coordinator?.notifyPlayEnded(playerId);
  }, [coordinator, playerId]);

  // Check if this player is currently playing
  const isCurrentlyPlaying = coordinator?.currentlyPlayingId === playerId;

  return {
    notifyPlayStarted,
    notifyPlayEnded,
    isCurrentlyPlaying,
    isCoordinated: coordinator !== null
  };
};

export default AudioPlayerCoordinatorProvider;
